import { CollisionGrid, type TilePos } from '@veriverse/game-logic';
import { TILE_SIZE } from '@veriverse/shared';
import { isBlockingObject, readProperties } from './properties.js';
import { BLOCKING_TILE_LAYERS, GID_MASK, SPRITE_TYPE, SPRITES_LAYER } from './rules.js';
import {
  type TiledLayer,
  type TiledMap,
  type TiledObject,
  type TiledObjectLayer,
  type TiledTileLayer,
} from './types.js';

export function findTileLayer(map: TiledMap, name: string): TiledTileLayer | undefined {
  return map.layers.find(
    (layer): layer is TiledTileLayer => layer.type === 'tilelayer' && layer.name === name,
  );
}

export function findObjectLayer(map: TiledMap, name: string): TiledObjectLayer | undefined {
  return map.layers.find(
    (layer): layer is TiledObjectLayer => layer.type === 'objectgroup' && layer.name === name,
  );
}

export function findLayer(map: TiledMap, name: string): TiledLayer | undefined {
  return map.layers.find((layer) => layer.name === name);
}

/** The object's type, whichever field the Tiled version used. */
export function objectType(object: TiledObject): string {
  return object.type || object.class || '';
}

/** Tile containing the object's anchor (top-left for rectangles, the point itself for points). */
export function objectTile(object: TiledObject): TilePos {
  return { x: Math.floor(object.x / TILE_SIZE), y: Math.floor(object.y / TILE_SIZE) };
}

/** Tile rectangle an object covers (points and spawns are 1×1; rotation is ignored). */
export function objectFootprint(object: TiledObject): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const { x, y } = objectTile(object);
  if (object.point || objectType(object) === 'spawn') return { x, y, w: 1, h: 1 };
  return {
    x,
    y,
    w: Math.max(1, Math.round(object.width / TILE_SIZE)),
    h: Math.max(1, Math.round(object.height / TILE_SIZE)),
  };
}

/** Number of bottom rows of a sprite that stand on the floor (property `base_rows`, default 1). */
export function spriteBaseRows(object: TiledObject): number {
  const { h } = objectFootprint(object);
  const value = readProperties(object).base_rows;
  const rows = typeof value === 'number' && Number.isInteger(value) ? value : 1;
  return Math.min(Math.max(rows, 1), h);
}

/**
 * Tile rectangle a sprite occupies on the floor: its bottom `base_rows` rows. The rows above are
 * only drawn (characters can stand behind them).
 */
export function spriteFootprint(object: TiledObject): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  const bounds = objectFootprint(object);
  const h = spriteBaseRows(object);
  return { x: bounds.x, y: bounds.y + bounds.h - h, w: bounds.w, h };
}

/** Tiles covered by a rectangle. */
function rectTiles({ x, y, w, h }: { x: number; y: number; w: number; h: number }): TilePos[] {
  const tiles: TilePos[] = [];
  for (let dy = 0; dy < h; dy += 1)
    for (let dx = 0; dx < w; dx += 1) tiles.push({ x: x + dx, y: y + dy });
  return tiles;
}

/** Tiles covered by an object (not clipped to the map; callers check bounds). */
export function footprintTiles(object: TiledObject): TilePos[] {
  return rectTiles(objectFootprint(object));
}

/** Floor tiles of a sprite (not clipped to the map). */
export function spriteFootprintTiles(object: TiledObject): TilePos[] {
  return rectTiles(spriteFootprint(object));
}

export function tileId(gid: number): number {
  return gid & GID_MASK;
}

const isOpenableDoor = (type: string) => type === 'toggle_door' || type === 'pin_door';

/**
 * Builds the authoritative walkability grid: any non-empty tile in a blocking layer blocks, and so
 * does every tile covered by a blocking object (`blocking` property or its type's default) and the
 * footprint (bottom rows) of every blocking sprite, except tiles with a non-blocking chair on them.
 * `toggleDoorsOpen` leaves toggle and PIN doors
 * out: any player can open a toggle door and anyone with the code a PIN door, so map checks treat
 * them as passable.
 */
export function buildCollisionGrid(
  map: TiledMap,
  options: { readonly toggleDoorsOpen?: boolean } = {},
): CollisionGrid {
  const grid = new CollisionGrid(map.width, map.height);
  for (const name of BLOCKING_TILE_LAYERS) {
    const layer = findTileLayer(map, name);
    if (!layer) continue;
    layer.data.forEach((gid, index) => {
      if (tileId(gid) !== 0) grid.setBlocked(grid.fromIndex(index), true);
    });
  }
  const seatTiles = new Set<number>();
  for (const object of findObjectLayer(map, 'objects')?.objects ?? []) {
    const type = objectType(object);
    if (!isBlockingObject(object, type)) {
      // A chair is sat on, so a cubicle sprite whose art covers it must not wall it in.
      if (type === 'chair')
        for (const tile of footprintTiles(object))
          if (grid.isInside(tile)) seatTiles.add(grid.index(tile));
      continue;
    }
    if (options.toggleDoorsOpen && isOpenableDoor(type)) continue;
    for (const tile of footprintTiles(object)) if (grid.isInside(tile)) grid.setBlocked(tile, true);
  }
  for (const object of findObjectLayer(map, SPRITES_LAYER)?.objects ?? []) {
    if (!isBlockingObject(object, SPRITE_TYPE)) continue;
    for (const tile of spriteFootprintTiles(object))
      if (grid.isInside(tile) && !seatTiles.has(grid.index(tile))) grid.setBlocked(tile, true);
  }
  return grid;
}

export interface Zone {
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Proximity chat reaches everyone inside this zone, however far apart (spec §6.7). */
  readonly chat: boolean;
}

/** Named rectangular areas in tile units (kitchen, hall, …). */
export function getZones(map: TiledMap): Zone[] {
  return (findObjectLayer(map, 'zones')?.objects ?? [])
    .filter((object) => objectType(object) === 'zone')
    .map((object) => ({
      key: object.name,
      x: Math.floor(object.x / TILE_SIZE),
      y: Math.floor(object.y / TILE_SIZE),
      width: Math.round(object.width / TILE_SIZE),
      height: Math.round(object.height / TILE_SIZE),
      chat: object.properties?.some((p) => p.name === 'chat_zone' && p.value === true) ?? false,
    }));
}

/** A separate area of a map (type `region` on `zones`), e.g. one office of several. */
export interface Region {
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Where the area really is (its weather comes from there); both or neither are set. */
  readonly location?: { readonly latitude: number; readonly longitude: number };
}

/** Regions in tile units, in map order. */
export function getRegions(map: TiledMap): Region[] {
  return (findObjectLayer(map, 'zones')?.objects ?? [])
    .filter((object) => objectType(object) === 'region')
    .map((object) => {
      const { x, y, w, h } = objectFootprint(object);
      const { latitude, longitude } = readProperties(object);
      const region = { name: object.name, x, y, width: w, height: h };
      return typeof latitude === 'number' && typeof longitude === 'number'
        ? { ...region, location: { latitude, longitude } }
        : region;
    });
}

/** The first region covering a tile, or `undefined`. */
export function regionAt(regions: readonly Region[], pos: TilePos): Region | undefined {
  return regions.find(
    (region) =>
      pos.x >= region.x &&
      pos.y >= region.y &&
      pos.x < region.x + region.width &&
      pos.y < region.y + region.height,
  );
}

/** A spawn point with its name, which teleporters refer to (`target_spawn`). */
export interface NamedSpawn extends TilePos {
  readonly name: string;
}

export function getNamedSpawns(map: TiledMap): NamedSpawn[] {
  return (findObjectLayer(map, 'zones')?.objects ?? [])
    .filter((object) => objectType(object) === 'spawn')
    .map((object) => ({ name: object.name, ...objectTile(object) }));
}

export function getSpawnPoints(map: TiledMap): TilePos[] {
  return (findObjectLayer(map, 'zones')?.objects ?? [])
    .filter((object) => objectType(object) === 'spawn')
    .map(objectTile);
}

export function zoneAt(zones: readonly Zone[], pos: TilePos): Zone | undefined {
  return zones.find(
    (zone) =>
      pos.x >= zone.x &&
      pos.y >= zone.y &&
      pos.x < zone.x + zone.width &&
      pos.y < zone.y + zone.height,
  );
}

export interface Desk {
  /** Tiled object id: stable, and what `users.desk_object_id` refers to. */
  readonly objectId: number;
  readonly name: string;
  /** The (blocked) tile the desk furniture occupies: its top-left tile. */
  readonly tile: TilePos;
  /**
   * Where the owner's avatar sits: the tile in front of the desk's footprint, on the side it faces
   * (its `rotation`: 0 = below, 90 = left, 180 = above, 270 = right).
   */
  readonly seat: TilePos;
}

/** Desks that can be assigned to people (spec §6.2). */
export function getDesks(map: TiledMap): Desk[] {
  return (findObjectLayer(map, 'objects')?.objects ?? [])
    .filter((object) => objectType(object) === 'desk')
    .map((object) => {
      const { x, y, w, h } = objectFootprint(object);
      const turns = ((Math.round((object.rotation ?? 0) / 90) % 4) + 4) % 4;
      const seat =
        turns === 1
          ? { x: x - 1, y }
          : turns === 2
            ? { x, y: y - 1 }
            : turns === 3
              ? { x: x + w, y }
              : { x, y: y + h };
      return { objectId: object.id, name: object.name, tile: { x, y }, seat };
    });
}

/** A furniture/interactable object as the simulation sees it: where it is and what it is. */
export interface MapObject {
  /** Tiled object id: stable across edits of the same map. */
  readonly id: number;
  readonly type: string;
  readonly name: string;
  /** Tiles the object covers. */
  readonly footprint: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  readonly properties: Readonly<Record<string, string | number | boolean>>;
}

/** Every object on the `objects` layer (spec §6.6), whatever its type. */
export function getMapObjects(map: TiledMap): MapObject[] {
  return (findObjectLayer(map, 'objects')?.objects ?? []).map((object) => ({
    id: object.id,
    type: objectType(object),
    name: object.name,
    footprint: objectFootprint(object),
    properties: readProperties(object),
  }));
}
